import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * 로그인 페이지
 */
const LoginPage: React.FC = () => {
  const [memberId, setMemberId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [currentPoster, setCurrentPoster] = useState(0);

  const { login, isAuthenticated, user, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const posters = ['/poster.jpg', '/poster2.jpg'];

  // 이미 로그인된 경우 state에 따라 리다이렉트
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.state === 'WAIT') {
        navigate('/wait', { replace: true });
      } else {
        navigate('/schedule', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // 초기 인증 확인 중에는 로딩 화면 표시
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await login({ memberId, password });
      // 로그인 성공 후 user 정보는 useEffect에서 처리
    } catch (err: any) {
      setError(err.response?.data?.message || '로그인에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePosterPrev = () => {
    setCurrentPoster((prev) => (prev === 0 ? posters.length - 1 : prev - 1));
  };

  const handlePosterNext = () => {
    setCurrentPoster((prev) => (prev === posters.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-accent/20 to-accent/40 dark:from-accent/10 dark:to-accent/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">Elloas</CardTitle>
          <CardDescription className="text-center">
            엘리시안 레이드 스케줄 관리 시스템
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="memberId">아이디</Label>
              <Input
                id="memberId"
                type="text"
                placeholder="아이디를 입력하세요"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                required
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">비밀번호</Label>
              <Input
                id="password"
                type="password"
                placeholder="비밀번호를 입력하세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? '로그인 중...' : '로그인'}
            </Button>

            <div className="w-full flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setPosterOpen(true)}
              >
                엘리시안 길드원 모집 포스터 보기
              </Button>
            </div>

            {/* 가입 문의 섹션 */}
            <Card className="w-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">가입 문의</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">길드마스터</div>
                  <div className="font-medium">참외</div>
                  <div className="text-muted-foreground">부길드마스터</div>
                  <div className="font-medium">숲쇽의슛샤슘</div>
                  <div className="text-muted-foreground">임원</div>
                  <div className="font-medium">파페</div>
                </div>
                <div className="flex flex-col items-center pt-2 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    오픈톡방 QR 코드
                  </p>
                  <img
                    src={`${process.env.PUBLIC_URL}/qr.jpg`}
                    alt="오픈톡방 QR"
                    className="w-28 h-28 rounded"
                  />
                </div>
              </CardContent>
            </Card>

            <p className="text-sm text-center text-muted-foreground">
              계정이 없으신가요?{' '}
              <Link to="/register" className="text-primary hover:underline">
                회원가입
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>

      {/* 모집 포스터 Dialog */}
      <Dialog open={posterOpen} onOpenChange={setPosterOpen}>
        <DialogContent className={`max-w-4xl max-h-[90vh] p-0 overflow-hidden flex flex-col ${
          theme === 'dark' ? 'bg-black/95' : 'bg-background'
        }`}>
          <VisuallyHidden>
            <DialogTitle>엘리시안 길드 모집 포스터</DialogTitle>
            <DialogDescription>
              엘리시안 길드원 모집 포스터를 확인하세요
            </DialogDescription>
          </VisuallyHidden>

          <div className="flex-1 overflow-y-auto">
            <img
              src={`${process.env.PUBLIC_URL}${posters[currentPoster]}`}
              alt={`엘리시안 길드 모집 포스터 ${currentPoster + 1}`}
              className="w-full h-auto"
            />
          </div>

          <div className={`flex justify-between items-center px-4 pb-4 pt-2 bg-gradient-to-t to-transparent ${
            theme === 'dark' ? 'from-black/90' : 'from-background'
          }`}>
            <div className="flex gap-2">
              <button
                onClick={handlePosterPrev}
                className={`p-2 rounded-full transition-colors ${
                  theme === 'dark'
                    ? 'bg-white/10 hover:bg-white/20'
                    : 'bg-foreground/10 hover:bg-foreground/20'
                }`}
                aria-label="이전 포스터"
              >
                <ChevronLeft className={`w-6 h-6 ${theme === 'dark' ? 'text-white' : 'text-foreground'}`} />
              </button>
              <button
                onClick={handlePosterNext}
                className={`p-2 rounded-full transition-colors ${
                  theme === 'dark'
                    ? 'bg-white/10 hover:bg-white/20'
                    : 'bg-foreground/10 hover:bg-foreground/20'
                }`}
                aria-label="다음 포스터"
              >
                <ChevronRight className={`w-6 h-6 ${theme === 'dark' ? 'text-white' : 'text-foreground'}`} />
              </button>
            </div>

            <div className="flex gap-2 items-center">
              {posters.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPoster(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    theme === 'dark'
                      ? index === currentPoster ? 'bg-white' : 'bg-white/40'
                      : index === currentPoster ? 'bg-foreground' : 'bg-foreground/40'
                  }`}
                  aria-label={`포스터 ${index + 1}번으로 이동`}
                />
              ))}
            </div>

            <button
              onClick={() => setPosterOpen(false)}
              className={`px-4 py-2 rounded-md border font-medium transition-colors ${
                theme === 'dark'
                  ? 'border-white/60 hover:border-white hover:bg-white/10 text-white'
                  : 'border-border hover:border-foreground hover:bg-accent text-foreground'
              }`}
            >
              닫기
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
